import { useEffect, useState } from "react"
import { useRunMessages } from "../../contexts/runMessages/RunMessageContext"
import { convertToLocalTime } from "../../utils/dateAndTimeUtils"
import { Spinner } from "@components/ui/spinner/Spinner"
import { RunMessageTooltip } from "@components/ui/tooltips/LiveTrackTooltips"
/**
 * Parent Component for Run Messages in components (live track, live track edit, site map w pig)
 * 
 * @param {number} selectedRun 
 * @param {number} numberOfMessages count of messages in live track update
 * @param {Date} dataUpdated date that data was last upated
 * @param {Boolean} notificationsPaused 
 *  
 * @returns {JSX.Element}

 */
export const RunMessages = ({selectedRun, numberOfMessages, dataUpdated, notificationsPaused, setNotificationsPaused, timezone}) => {
    const {messages, fetchMessages, submitting} = useRunMessages(setNotificationsPaused)
    const [loadingMessages, setLoadingMessages] = useState(false);

    const refreshMessages = async (showLoading = true) => {
        if (!selectedRun) return;
        if (showLoading) setLoadingMessages(true);
        try {
            await fetchMessages(selectedRun);
        } finally {
            if (showLoading) setLoadingMessages(false);
        }
    };

    useEffect(() => {
        refreshMessages(true);
    }, [selectedRun]); //eslint-disable-line
    
    //compare messages from live track update with number of messages currently being displayed
    //if live track update had a higher count, fetch messages from API (silently, no spinner)
    useEffect(() => {
        if (messages.length === 0 || numberOfMessages > messages.length) {
            refreshMessages(false);
        }
    }, [numberOfMessages]); //eslint-disable-line
    
    return (
        <div>
            <h1 className="text-center text-md font-semibold mb-4">
                Messages
                <RunMessageTooltip />
            </h1>

            {/* display info about the messages */}

            <div className="flex flex-col md:flex-row md:justify-between w-full">

                {/* notifications paused message */}
                <p className="text-red-500 text-md font-bold mb-1 md:mb-0">
                    {notificationsPaused ? <><i className="fa-solid fa-circle-exclamation"></i> Notifications are paused. Estimated pass times may be inaccurate.</> : null}
                </p>
             
                {/* show when messages were last checked */}
                {dataUpdated ? 
                    <p className="justify-self-end text-sm mb-1 md:mb-0 md:justify-self-start">
                        <span className="font-semibold">Last updated: </span> 

                        {new Intl.DateTimeFormat('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                            timeZone: timezone.value
                        }).format(dataUpdated)}
                        {` (${timezone.abbr})`}
                    </p>
                : null }
            </div>
            
            {/* display messages */}
            <div className="border border-gray-200 h-56 flex justify-center">

                {loadingMessages ? (
                    <div className="self-center flex items-center gap-2">
                        <Spinner className="text-sm" />
                        <span className="text-sm">Loading messages...</span>
                    </div>
                ) : messages.length === 0 ? (
                    <p className="self-center">There are no messages for this run</p>
                ) : (
                    <div className="overflow-y-scroll max-h-full w-full">

                        {/* messages */}
                        {messages.map((message, i) => (

                            <div 
                                className="mx-4 mt-4 mb-6"
                                key={message.RMID ?? i}
                            >
                                {/* sender name and date */}
                                <p className="text-sm font-semibold text-gray-700 mb-1 flex gap-4 ">
                                    <span>{message.CreatedBy.First_Name} {message.CreatedBy.Last_Name}</span>
                                    <span>{convertToLocalTime(message.CreatedAt, timezone.value)} ({timezone.abbr})</span>
                                </p>

                                {/* message */}
                                <div className="my-1 min-h-16 border border-black rounded-md p-4 max-w-max ">{message.Message}</div>
                            </div> 
                        ))}
                        
                    </div>
                )}

                
            </div>

            {/* tells user if messages are currently being sent */}
            {/* Why use this instead of showLoading? showLoading overlays on top of whole screen, and sometimes the emails take a long time to send. We don't want to block the user from doing other things. This is less intrusive but still gives them feedback. */}
            <div className="flex">
                {submitting ? (
                    <div className="flex items-center text-sm mt-4 md:mb-0 ml-0 md:ml-auto">
                        <Spinner className="mr-2 text-sm" />
                        {submitting}
                    </div>
                ) : null}
            </div>

        </div>
    )
}